import {
    ActionIcon,
    Button,
    ColorPicker,
    ColorSwatch,
    Divider,
    Group,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Title,
    UnstyledButton,
} from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { modals } from "@mantine/modals";
import { IconColorPicker, IconImageInPicture, IconTrash, IconUpload } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { DevtoolCard } from "routes/_auth/_dev/devtools";
import { Content } from "shared/components/global/Content";
import { FileItem } from "shared/components/global/FileItem";
import { useCustomer } from "shared/graphql/cust";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { useUploadQueue } from "shared/utils/upload";
import { graphql } from "src/gql";

const Page = () => {
    const isMobile = useIsMobile();

    function handleEditIcon() {
        modals.open({
            title: <Title order={3}>Upload Icon</Title>,
            children: <EditIconPanel />,
        });
    }

    function handleEditBrandColor() {
        modals.open({
            title: <Title order={3}>Edit Brand Color</Title>,
            children: <EditColorPanel />,
        });
    }

    return (
        <Content>
            <Content.Heading backable>Customer Branding</Content.Heading>
            <SimpleGrid cols={isMobile ? 1 : 2}>
                <UnstyledButton onClick={handleEditIcon}>
                    <DevtoolCard rightSection={<IconImageInPicture size={24} />}>Icon</DevtoolCard>
                </UnstyledButton>
                <UnstyledButton onClick={handleEditBrandColor}>
                    <DevtoolCard rightSection={<IconColorPicker size={24} />}>Brand Color</DevtoolCard>
                </UnstyledButton>
            </SimpleGrid>
        </Content>
    );
};

const EditIconPanel = () => {
    const save = useGraphqlMutation(muts.setIcon);
    const [iconId, setIconId] = useState<string>();
    const uploader = useUploadQueue({
        onUploaded([{ id }]) {
            setIconId(id);
        },
    });

    return (
        <Stack>
            {!iconId && (
                <Dropzone
                    loading={uploader.isLoading}
                    accept={[MIME_TYPES.jpeg, MIME_TYPES.png, MIME_TYPES.svg, MIME_TYPES.gif]}
                    onDrop={([file]) => uploader.enqueue(file)}
                >
                    <Stack align="center" justify="center" p="xl">
                        <IconUpload size={32} />
                        <div style={{ textAlign: "center" }}>
                            <Text size="lg" inline>
                                Upload File
                            </Text>
                            <Text size="sm" c="dimmed" inline mt={7}>
                                jpeg, png, svg, or gif
                            </Text>
                        </div>
                    </Stack>
                </Dropzone>
            )}
            <Suspense>
                {!!iconId && (
                    <FileItem fileId={iconId}>
                        <FileItem.Action>
                            <ActionIcon variant="transparent" onClick={() => setIconId(undefined)}>
                                <IconTrash size={16} />
                            </ActionIcon>
                        </FileItem.Action>
                    </FileItem>
                )}
            </Suspense>
            <Group justify="flex-end">
                <Button
                    variant="default"
                    disabled={!iconId}
                    loading={!save.isIdle}
                    onClick={() => {
                        if (!iconId) return;
                        save.mutateAsync({ fileId: iconId })
                            .then(() => window.location.reload())
                            .catch(notify.catch);
                    }}
                >
                    Save and Reload
                </Button>
            </Group>
        </Stack>
    );
};

const EditColorPanel = () => {
    const { data: customer } = useCustomer();
    const save = useGraphqlMutation(muts.setColor);
    const [color, setColor] = useState(customer.primaryColor);

    return (
        <Stack>
            <ColorPicker fullWidth value={color} onChange={setColor} />
            <Group justify="space-between">
                <TextInput label="Hex" value={color} onChange={(e) => setColor(e.target.value)} />
                <ColorSwatch color={color} mt="auto" />
            </Group>
            <Divider mx="-md" />
            <Group justify="flex-end">
                <Button
                    variant="default"
                    loading={!save.isIdle}
                    onClick={() =>
                        void save.mutateAsync({ hex: color }).then(() => {
                            void qk.invalidate("customer");
                            modals.closeAll();
                        })
                    }
                >
                    Save
                </Button>
            </Group>
        </Stack>
    );
};

const muts = {
    setIcon: graphql(`
        mutation DevtoolBrandingSetIcon($fileId: UUID!) {
            updateCustomerIcon(iconId: $fileId) {
                id
            }
        }
    `),
    setColor: graphql(`
        mutation DevtoolBrandingSetColor($hex: String!) {
            updateCustomerColor(primaryColor: $hex) {
                id
            }
        }
    `),
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/branding")({
    component: Page,
});
